import { ChameleonRequest } from "@repo/chameleon-request";
import {
  addProductInitialiser,
  removeProductInitialiser,
  updateProductInitialiser,
  initialiseCheckoutInitialiser,
  getBagInitialiser,
  getFreeDeliveryQualificationInitialiser,
  getNextDayDeliveryInitialiser,
  getProductsInitialiser,
} from "./public-methods";
import { deleteAllBagCookies } from "./utils/bag-cookies";
import { SummaryState, cache } from "./utils/cache";
import { initialiseMethod } from "./utils/method-initialiser";

export type BagSdkConfig = {
  getIdTokenClaims: () => Promise<{
    customerId: string;
    customerAnonymousId: string;
  }>;
  chameleonRequestInstance: ChameleonRequest;
  getAuthHeader: () => Promise<string>;
  isAuthenticated: () => boolean;
  lang: string;
  keyStoreDataversion: string;
  store: string;
  country: string;
  originCountry: string;
  currency: string;
  sizeSchema: string;
  shop: string;
};

export type BagSdkInstance = {
  initialSummary: SummaryState;
  addProduct: ReturnType<typeof addProductInitialiser>;
  removeProduct: ReturnType<typeof removeProductInitialiser>;
  updateProduct: ReturnType<typeof updateProductInitialiser>;
  initialiseCheckout: ReturnType<typeof initialiseCheckoutInitialiser>;
  getNextDayDelivery: ReturnType<typeof getNextDayDeliveryInitialiser>;
  getFreeDeliveryQualification: ReturnType<
    typeof getFreeDeliveryQualificationInitialiser
  >;
  getBag: ReturnType<typeof getBagInitialiser>;
  getProducts: ReturnType<typeof getProductsInitialiser>;
};

export const initialiseBagSdk = async (
  config: BagSdkConfig,
): Promise<BagSdkInstance> => {
  const { store, country, isAuthenticated } = config;
  const hasSignedOut =
    window.sessionStorage.getItem("AsosAuth.SignedOut") === "true" || false;

  if (hasSignedOut) {
    deleteAllBagCookies({ store, country });
    cache.clear();
    window.sessionStorage.removeItem("AsosAuth.SignedOut");
  }

  const existingSummary = await cache.getSummary(config);
  const authenticated = isAuthenticated();
  const getBag = initialiseMethod(config, getBagInitialiser);

  if (!authenticated) {
    if (
      !existingSummary ||
      (existingSummary.hasExpired && !existingSummary.bagId)
    ) {
      await cache.init(config);
    }
  }

  const isAuthenticatedWithoutFreshSummary =
    authenticated && (!existingSummary || existingSummary?.hasExpired);

  const isExpiredStateWithBag =
    existingSummary?.hasExpired && existingSummary?.bagId;

  const isAuthenticatedWithAnonymousBag =
    authenticated &&
    (await config.getIdTokenClaims()).customerId !==
      existingSummary?.customerId;

  try {
    if (
      isAuthenticatedWithoutFreshSummary ||
      isExpiredStateWithBag ||
      isAuthenticatedWithAnonymousBag
    ) {
      await getBag({
        expand: false,
      });
    }
  } catch (error: unknown) {
    // no-op: getBag handles errors and updates state accordingly. We only care about fresh state at this point
  }

  const summary = await cache.getSummary(config);

  if (!summary || summary?.hasExpired) {
    throw new Error("Failed to initialise the Bag SDK with a valid summary");
  }

  return {
    initialSummary: summary,
    addProduct: initialiseMethod(config, addProductInitialiser),
    removeProduct: initialiseMethod(config, removeProductInitialiser),
    updateProduct: initialiseMethod(config, updateProductInitialiser),
    initialiseCheckout: initialiseMethod(config, initialiseCheckoutInitialiser),
    getNextDayDelivery: initialiseMethod(config, getNextDayDeliveryInitialiser),
    getFreeDeliveryQualification: initialiseMethod(
      config,
      getFreeDeliveryQualificationInitialiser,
    ),
    getBag,
    getProducts: initialiseMethod(config, getProductsInitialiser),
  };
};
